import { Navigate, Outlet, createBrowserRouter, useLocation, useRouteError, type RouteObject } from 'react-router';
import { AppShell } from '@/components/layout/app-shell';
import { Button, EmptyState, LoadingBlock, UnsavedChangesGuard } from '@/components/ui';
import { page } from '@/lib/lazy-route';
import * as accounting from '@/modules/accounting/routes'; // G1 (ledger core)
import * as admin from '@/modules/admin/routes';
import * as agreements from '@/modules/agreements/routes';
import * as ai from '@/modules/ai/routes'; // Phase F (F5): AI assistant
import * as automation from '@/modules/automation/routes';
import * as banking from '@/modules/banking/routes'; // G3/G4 (g-banking)
import * as billing from '@/modules/billing/routes';
import * as crm from '@/modules/crm/routes';
import * as docs from '@/modules/docs/routes';
import * as extension from '@/modules/extension/routes';
import * as hr from '@/modules/hr/routes';
import * as integrations from '@/modules/integrations/routes'; // Phase B (B9/B10)
import * as licences from '@/modules/licences/routes';
import { legalRoutes } from '@/modules/legal/routes'; // I5 (trust track): public legal pages
import * as monitoring from '@/modules/monitoring/routes'; // Phase M (m-core): Denwen MSP Monitor
import { mobileRoute } from '@/modules/mobile/routes'; // Phase F mobile track (F4)
import * as organizations from '@/modules/organizations/routes';
import * as payables from '@/modules/payables/routes'; // G2 (payables)
import * as payroll from '@/modules/payroll/routes'; // Phase H: UK payroll
import * as platform from '@/modules/platform/routes';
import * as platformConsole from '@/modules/platform-console/routes'; // Phase I (I-D): the platform console
import { portalRoutes } from '@/modules/portal/routes';
import { RequirePortal } from '@/modules/portal/require-portal'; // J6 (F-J6-6): deep links survive sign-in
import * as projects from '@/modules/projects/routes';
import * as purchasing from '@/modules/purchasing/routes';
import * as reports from '@/modules/reports/routes';
import * as schedule from '@/modules/schedule/routes';
import * as signup from '@/modules/signup/routes'; // Phase I (I-A): sign-up and onboarding
import * as tickets from '@/modules/tickets/routes';
import * as time from '@/modules/time/routes';
import { useHostInfo } from '@/modules/branding/api';
import { useAuth } from './auth';

// Each module exports `routes` (pages in the app shell) and `orgRoutes` (pages inside an
// organization). This file only composes them; modules own their own routes. Every page is
// lazy-loaded, so the initial bundle holds only the shell.
// Phase D CRM track: crm (D8), licences (D10), purchasing (D11).
const MODULES = [reports, organizations, tickets, time, docs, agreements, billing, projects, schedule, hr, automation, admin, platform, extension, integrations, crm, licences, purchasing, ai, accounting, monitoring, payables, banking, payroll, signup, platformConsole]; // G2 payables, G3/G4 banking, Phase H payroll, Phase I sign-up and console

function RequireStaff() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const host = useHostInfo();
  if (loading || host.isLoading) return <LoadingBlock className="h-full" />;
  // Phase I (I-C): a custom portal domain only serves the customer portal.
  if (host.data?.kind === 'custom') return <Navigate to="/portal" replace />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (user.kind === 'portal') return <Navigate to="/portal" replace />;
  return <Outlet />;
}

function RouteError() {
  const error = useRouteError() as { status?: number; message?: string } | undefined;
  return (
    <EmptyState
      className="h-full"
      title={error?.status === 404 ? 'Page not found' : 'Something went wrong'}
      description={error?.status === 404 ? 'Check the address, or use search (Ctrl K).' : 'The error has been logged. Try reloading the page.'}
      action={<Button onClick={() => window.location.assign('/')}>Go home</Button>}
    />
  );
}

const staffRoutes: RouteObject[] = [
  ...MODULES.flatMap((m) => m.routes),
  { path: 'organizations/:orgId', lazy: page(() => import('@/modules/organizations/org-layout'), (m) => m.OrgLayout), children: MODULES.flatMap((m) => m.orgRoutes) },
  { path: 'account', lazy: page(() => import('@/modules/account/account-page'), (m) => m.AccountPage) },
  { path: '*', element: <EmptyState title="Page not found" description="Check the address, or use search (Ctrl K)." /> },
];

const authPages = () => import('@/modules/auth/auth-pages');

export const router = createBrowserRouter([
  {
    // Shown while the first page's chunk loads.
    hydrateFallbackElement: <LoadingBlock className="h-dvh" />,
    // settings-save: one navigation blocker for every page with unsaved settings.
    element: <UnsavedChangesGuard />,
    children: [
      { path: '/login', lazy: page(authPages, (m) => m.LoginPage) },
      { path: '/forgot-password', lazy: page(authPages, (m) => m.ForgotPasswordPage) },
      { path: '/reset-password', lazy: page(authPages, (m) => m.ResetPasswordPage) },
      { path: '/verify-email', lazy: page(() => import('@/modules/account/verify-email'), (m) => m.VerifyEmailPage) }, // K-2: the emailed verify link
      ...signup.publicRoutes, // Phase I (I-A): /signup and the emailed /signup/verify link
      // Phase A identity: portal sign-in with the portal's own SSO providers.
      { path: '/portal/login', lazy: page(authPages, (m) => m.PortalLoginPage) },
      // K4: confirming a sign-in to an application that uses Denwen MSP as its OpenID provider.
      { path: '/oidc/continue', lazy: page(() => import('@/modules/security/oidc-continue-page'), (m) => m.OidcContinuePage) },
      // B4 (vault track): public one-time password share link (token in the URL fragment).
      { path: '/share', lazy: page(() => import('@/modules/docs/share-page'), (m) => m.SharePage) },
      // Phase D CRM track (D9): the customer quote link (token only, no sign-in).
      { path: '/q/:token', lazy: page(() => import('@/modules/quoting/public-quote-page'), (m) => m.PublicQuotePage) },
      // Phase M (m-ui): a client's public status page (slug only, no sign-in).
      monitoring.publicStatusRoute,
      // I5 (trust track): terms, DPA, privacy and the other legal pages (no sign-in).
      ...legalRoutes,
      { path: '/portal', element: <RequirePortal />, errorElement: <RouteError />, children: portalRoutes },
      // F4 (mobile track): the technician PWA. Its own shell and sign-in gate (it opens offline).
      { ...mobileRoute, errorElement: <RouteError /> },
      {
        element: <RequireStaff />,
        errorElement: <RouteError />,
        children: [{ path: '/', element: <AppShell />, errorElement: <RouteError />, children: staffRoutes }],
      },
    ],
  },
]);
